import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { getBilling, cancelSubscription, resumeSubscription } from "@/lib/api";
import { StripePricingTable } from "@/components/StripePricingTable";
import { toast } from "sonner";

const PLAN_LABELS = {
  free: "Free",
  onetime: "One-time",
  monthly: "Business Unlimited",
  agency: "Agency White-Label",
};

const STATUS_LABELS = {
  active: { text: "Active", tone: "default" },
  trialing: { text: "Trial", tone: "default" },
  past_due: { text: "Payment failed — retrying", tone: "destructive" },
  unpaid: { text: "Unpaid — access paused", tone: "destructive" },
  canceled: { text: "Canceled", tone: "outline" },
  incomplete: { text: "Pending first payment", tone: "outline" },
  incomplete_expired: { text: "Expired", tone: "outline" },
};

export default function Billing() {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { setBilling(await getBilling()); } catch {}
  };

  useEffect(() => { refresh(); }, []);

  const onCancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    setBusy(true);
    try {
      await cancelSubscription();
      toast.success("Subscription canceled. Access stays active until your period ends.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setBusy(false);
    }
  };

  const onResume = async () => {
    setBusy(true);
    try {
      await resumeSubscription();
      toast.success("Subscription resumed.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume subscription");
    } finally {
      setBusy(false);
    }
  };

  const planLabel = billing
    ? (billing.plan === "monthly" || billing.plan === "agency"
        ? PLAN_LABELS[billing.plan]
        : billing.credits > 0
          ? `${billing.credits} credit${billing.credits === 1 ? "" : "s"}`
          : "Free")
    : "—";

  const status = billing?.subscription_status;
  const statusMeta = status ? STATUS_LABELS[status] : null;
  const isSubscribed = billing?.plan === "monthly" || billing?.plan === "agency";
  const scheduledToCancel = !!billing?.subscription_cancel_at_period_end;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-8">
          <div className="tag-label text-[#E27D60]">Billing</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Plans & checkout</h1>
        </div>

        <div className="rounded-3xl bg-white border border-black/5 p-6 mb-8 bento-shadow flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="tag-label text-[#1E392A]/55">Current plan</div>
            <div className="font-display text-2xl mt-1" data-testid="current-plan-label">{planLabel}</div>
            {billing?.subscription_expires_at && (
              <div className="text-xs text-[#1E392A]/55 mt-0.5">
                {scheduledToCancel ? "Access ends" : "Renews"} {new Date(billing.subscription_expires_at).toLocaleDateString()}
              </div>
            )}
            {statusMeta && (
              <div className="text-xs text-[#1E392A]/55 mt-0.5">
                Stripe status: <span className="font-medium text-[#1E392A]/80">{statusMeta.text}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={billing?.has_access ? "default" : "outline"} className="rounded-full px-3 py-1.5" data-testid="access-badge">
              {billing?.has_access ? "Active access" : "No active access"}
            </Badge>
            {billing?.can_white_label && (
              <Link to="/branding" className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white text-sm h-9 px-4 inline-flex items-center" data-testid="open-branding-link">
                Brand settings
              </Link>
            )}
          </div>
        </div>

        {isSubscribed && (
          <div className="rounded-3xl bg-white border border-black/5 p-6 mb-8 bento-shadow">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="tag-label text-[#1E392A]/55">Manage subscription</div>
                <div className="text-sm text-[#1E392A]/70 mt-1">
                  {scheduledToCancel
                    ? "Your subscription is set to cancel at the end of the current period."
                    : "Cancel anytime — access stays active until the end of your paid period."}
                </div>
              </div>
              {scheduledToCancel ? (
                <Button
                  onClick={onResume}
                  disabled={busy}
                  data-testid="resume-subscription-btn"
                  className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white h-10 px-5"
                >
                  {busy ? "Working…" : "Resume subscription"}
                </Button>
              ) : (
                <Button
                  onClick={onCancel}
                  disabled={busy}
                  data-testid="cancel-subscription-btn"
                  variant="outline"
                  className="rounded-full border-[#1E392A]/20 hover:bg-[#F7F5F0] h-10 px-5"
                >
                  {busy ? "Working…" : "Cancel subscription"}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="rounded-3xl bg-white border border-black/5 p-6 md:p-8 bento-shadow">
          <div className="text-center max-w-xl mx-auto mb-6">
            <div className="tag-label text-[#E27D60]">Subscribe</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">
              {isSubscribed ? "Change plan" : "Pick a plan"}
            </h2>
            <p className="text-sm text-[#1E392A]/60 mt-2">
              Pay securely with Stripe. Access activates the moment payment confirms.
            </p>
          </div>
          {user ? (
            <StripePricingTable email={user.email} userId={user.user_id} />
          ) : (
            <div className="text-center text-sm text-[#1E392A]/60 py-10">
              Loading your account…
            </div>
          )}
        </div>

        <p className="text-xs text-[#1E392A]/45 mt-6">
          Payments processed securely by Stripe. Cancel monthly plans any time —
          your access stays active through the end of the paid period.
        </p>
      </main>
    </div>
  );
}
